// @ts-strict-ignore
import useForm, { type SubmitPromise, type UseFormResult } from "@dashboard/hooks/useForm";
import type * as React from "react";

import { type FormId } from "./types";

export type CheckIfSaveIsDisabledFnType<T> = (data: T) => boolean;

interface FormProps<TData, TErrors>
  extends Omit<React.HTMLProps<HTMLFormElement>, "onSubmit" | "children"> {
  children: (props: UseFormResult<TData>) => React.ReactNode;
  confirmLeave?: boolean;
  initial?: TData;
  resetOnSubmit?: boolean;
  onSubmit?: (data: TData) => SubmitPromise<TErrors[]> | void;
  formId?: FormId;
  checkIfSaveIsDisabled?: CheckIfSaveIsDisabledFnType<TData>;
  mergeData?: boolean;
  /** Override default deep merge when `mergeData` is true. */
  mergeFunc?: (prevData: TData, prevState: TData, data: TData) => TData;
}

/** @deprecated Use react-hook-form instead */
function Form<TData, Terrors>({
  children,
  initial,
  resetOnSubmit,
  onSubmit,
  confirmLeave = false,
  formId,
  checkIfSaveIsDisabled,
  disabled,
  mergeData,
  mergeFunc,
  ...rest
}: FormProps<TData, Terrors>) {
  const renderProps = useForm(initial, onSubmit, {
    confirmLeave,
    formId,
    checkIfSaveIsDisabled,
    disabled,
    mergeData,
    mergeFunc,
  });

  function handleSubmit(event?: React.FormEvent<HTMLFormElement>, cb?: () => void) {
    const { reset, submit } = renderProps;

    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (cb) {
      cb();
    }

    if (resetOnSubmit) {
      reset();
    }

    submit();
  }

  return (
    <form {...rest} onSubmit={handleSubmit}>
      {children(renderProps)}
    </form>
  );
}
Form.displayName = "Form";

export default Form;
